import { ApolloError } from '@apollo/client'
import { Button, LinkButton, Tooltip, grey } from '@pelotoncycle/design-system'
import { usePartnerUpdate } from 'data/hooks'
import { Partner_partner as TPartner } from 'data/queries/types/Partner'
import { EligibilityMethodTypes, PartnerInput } from 'data/types/graphql-global-types'
import { BaseSyntheticEvent, useEffect, useMemo, useRef } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AlertVariants, Box, Flex, useAlerts } from 'ui/components'
import { serializePartner } from 'utils'
import {
  DOMAIN_DEFAULT,
  DateFormatSelect,
  DomainInputsSection,
  EligibilityMethodSelect,
  FormInput,
  PartnerEnrollmentOptimizationCheckbox,
  PartnerSlugInput2,
} from './Fields'
import { FormSection } from './FormSection'
import { StyledForm } from './StyledForm'
import { StyledFormActionsContainer } from './StyledFormActionsContainer'

type TEligibilityFormProps = {
  partner: TPartner
  previousStep: string
  onSuccess?: () => void
}

// Only submit relevant fields for given eligibility method
const normalizeDataForSubmit = (dirtyFields: string[], input: FormInput) => {
  const initialValue = {} as Partial<
    Pick<
      PartnerInput,
      'dateFormat' | 'domains' | 'slug' | 'enrollmentOptimization' | 'eligibilityMethod'
    >
  >

  return dirtyFields.reduce((acc, field) => {
    switch (field) {
      case 'dateFormat':
        acc.dateFormat = serializePartner.dateFormat(input.dateFormat)
        break
      case 'domains':
        acc.domains = serializePartner.domains(input.domains)
        break
      case 'slug':
        acc.slug = input[field]
        break
      case 'enrollmentOptimization':
        acc.enrollmentOptimization = input[field]
        break
      case 'eligibilityMethod':
        acc.eligibilityMethod = input[field]
        break
      default:
        break
    }

    return acc
  }, initialValue)
}

const PartnerEligibilityForm = ({
  partner,
  onSuccess,
  previousStep,
}: TEligibilityFormProps) => {
  const { t } = useTranslation()
  const { name, slug, domains, dateFormat, enrollmentOptimization, eligibilityMethod } =
    partner

  const { addAlert } = useAlerts()
  const { updatePartner, loading: updatePartnerLoading } = usePartnerUpdate({
    partnerId: partner.id,
  })

  // react-hook-form requires objects for useFieldArray so it can add an id per entry for tracking/state
  const normalizedDomains = useMemo(
    () =>
      domains && domains.length ? domains.map(d => ({ domain: d })) : [DOMAIN_DEFAULT],
    [domains],
  )

  const defaultValues = useMemo(
    () => ({
      slug,
      dateFormat,
      domains: normalizedDomains,
      enrollmentOptimization,
      eligibilityMethod,
    }),
    [slug, dateFormat, normalizedDomains, enrollmentOptimization, eligibilityMethod],
  )
  const { control, handleSubmit, formState, reset, setValue, trigger, watch, getValues } =
    useForm<FormInput>({
      defaultValues,
    })

  const { isDirty, dirtyFields } = formState
  const isLoading = updatePartnerLoading
  const shouldDisableSubmit = isLoading || !isDirty

  // watch value is undefined until changed in form, so it needs to default to the value persisted on partner
  const watchEligibilityMethod = watch('eligibilityMethod') || eligibilityMethod

  const eligibilityMethodIsDBO = watchEligibilityMethod === EligibilityMethodTypes.DBO
  const eligibilityMethodIsSSO = watchEligibilityMethod === EligibilityMethodTypes.SSO

  const prevEligibilityMethod = useRef(eligibilityMethod)

  useEffect(
    function resetFieldsWhenEligibilityMethodChanges() {
      const hasChangedEligibilityMethod =
        watchEligibilityMethod !== prevEligibilityMethod.current
      if (hasChangedEligibilityMethod) {
        const isReturningToOriginalEligibilityMethod =
          eligibilityMethod === watchEligibilityMethod

        // Reset the whole form back to persisted defaults. This clears the now-stale
        // method-specific fields (domains, slug) along with their dirty/touched state.
        const currentValues = getValues()
        reset(defaultValues)

        // The method-agnostic fields should survive any method change, so re-apply the
        // user's current edits after the reset. shouldDirty when they differ from persisted,
        // so the change is still tracked in dirtyFields and included on save.
        const methodAgnosticFields = ['enrollmentOptimization', 'dateFormat'] as const
        methodAgnosticFields.forEach(field => {
          if (currentValues[field] !== defaultValues[field]) {
            setValue(field, currentValues[field], { shouldDirty: true })
          }
        })

        // Moving to a new method: keep eligibilityMethod dirty so it's included on save.
        // Returning to the original: reset() already restored it as clean, so leave it.
        if (!isReturningToOriginalEligibilityMethod) {
          setValue('eligibilityMethod', watchEligibilityMethod, {
            shouldDirty: true,
            shouldTouch: true,
          })
        }
      }

      prevEligibilityMethod.current = watchEligibilityMethod
    },
    [
      reset,
      setValue,
      getValues,
      defaultValues,
      eligibilityMethod,
      watchEligibilityMethod,
    ],
  )

  const handleUpdatePartner: SubmitHandler<FormInput> = input => {
    const fieldsToUpdate = Object.keys(dirtyFields).filter(field =>
      Boolean(dirtyFields[field as keyof FormInput]),
    )
    const normalizedFields = normalizeDataForSubmit(fieldsToUpdate, input)
    const normalizedInput = {
      ...normalizedFields,
      eligibilityMethod: watchEligibilityMethod, // include eligibilityMethod on all updates
    }

    updatePartner({ input: normalizedInput })
      .then(({ data: responseData }) => {
        if (responseData?.partnerUpdate?.requestOk) {
          addAlert({
            variant: AlertVariants.success,
            message: t('partner.update_succeeded', { name }),
            autoClose: 8000,
          })

          if (onSuccess) onSuccess()
        }
      })
      .catch(e => {
        let error = ''
        if (typeof e === 'string') error = e
        if (e instanceof ApolloError && typeof e.message === 'string') error = e.message
        addAlert({
          variant: AlertVariants.error,
          message: t('partner.update_failed', { error }),
          autoClose: 8000,
        })
      })
  }

  const submitForm = (e: BaseSyntheticEvent) => {
    handleSubmit(handleUpdatePartner)(e).catch(err => {
      /* eslint-disable-next-line */
      console.log('err ==>', err)
    })
  }

  let formFields = null

  if (eligibilityMethodIsDBO) {
    formFields = (
      <Box marginTop="24px">
        <FormSection
          title={t('partner.dbo_domains')}
          subtext={t('partner.dbo_domains_subtext')}
        >
          <DomainInputsSection control={control} trigger={trigger} />
        </FormSection>
      </Box>
    )
  } else if (eligibilityMethodIsSSO) {
    formFields = <PartnerSlugInput2 control={control} />
  }

  return (
    <StyledForm data-testid="eligibility-form">
      <form onSubmit={submitForm}>
        <FormSection title={t('partner.enrollment')}>
          <Flex alignItems="center">
            <PartnerEnrollmentOptimizationCheckbox control={control} />
            <Tooltip textColor={grey[80]} margin="0 8px" position="right">
              {t('partner.enrollment_tooltip')}
            </Tooltip>
          </Flex>
        </FormSection>
        <FormSection title={t('partner.eligibility')}>
          <EligibilityMethodSelect control={control} />
          <>
            <DateFormatSelect control={control} />
            {formFields}
          </>
        </FormSection>

        <StyledFormActionsContainer>
          <Box width="126px" marginRight="16px">
            <LinkButton
              color="dark"
              variant="outline"
              component={Link}
              to={previousStep}
              text={t('partner.go_back')}
              width="adaptive"
            />
          </Box>
          <Box width="126px">
            <Button
              type="submit"
              data-testid="partnerEligibilityUpdate"
              width="adaptive"
              isDisabled={shouldDisableSubmit}
            >
              {t('partner.save_and_continue')}
            </Button>
          </Box>
        </StyledFormActionsContainer>
      </form>
    </StyledForm>
  )
}

export { PartnerEligibilityForm }
